import React from "react";
import {
  declareAiCapability,
  useProvideAiCapability,
} from "../../ai-omnibar/ai-capability-registry";

/**
 * What the fax dashboard can do for the AI omnibar.
 *
 * The patient picker is left out on purpose: it takes a patient id from a typeahead, and turning a
 * name into that id would mean guessing which patient was meant.
 */
declareAiCapability({
  id: "faxDashboard.filter",
  title: "Filter the fax dashboard",
  effect: "prefill",
  description:
    "Sets the filters on Comm Center > Fax Dashboard: the sent date range and the fax's status. Only works while that page is open.",
  location: "the filter bar of the Comm Center > Fax Dashboard page",
  scope: "page",
  permissionKey: "view_communication_center",
  params: [
    {
      name: "from",
      type: "date",
      required: false,
      description: "Earliest sent date to include, as yyyy-MM-dd.",
    },
    {
      name: "to",
      type: "date",
      required: false,
      description: "Latest sent date to include, as yyyy-MM-dd.",
    },
    {
      name: "status",
      type: "enum",
      required: false,
      multiple: true,
      description: "The state of the fax.",
      options: [
        { value: "success", label: "Success" },
        { value: "processing", label: "Processing" },
        { value: "transmitting", label: "Transmitting" },
        { value: "failed", label: "Failed" },
      ],
    },
  ],
  examples: ["failed faxes this week", "faxes sent yesterday"],
});

export function useOpenFaxDashboardAiCapabilities(params: {
  /** The page's own setValue, typed loosely: this hook only sets keys it declares. */
  setValue: (key: string, value: unknown) => void;
}): void {
  const { setValue } = params;

  const setValueRef = React.useRef(setValue);
  setValueRef.current = setValue;

  useProvideAiCapability("faxDashboard.filter", (args) => {
    const applied: string[] = [];
    const asList = (value: unknown) => (Array.isArray(value) ? value : [value]);

    if (args.from !== undefined) {
      setValueRef.current("createdAtFrom", String(args.from));
    }

    if (args.to !== undefined) {
      setValueRef.current("createdAtTo", String(args.to));
    }

    if (args.from !== undefined || args.to !== undefined) {
      applied.push([args.from, args.to].filter(Boolean).join(" to "));
    }

    if (args.status !== undefined) {
      const allowed = ["success", "processing", "transmitting", "failed"];
      const statuses = asList(args.status).map((one) => {
        const match = allowed.find((candidate) => candidate === String(one).toLowerCase());

        if (match === undefined) {
          throw new Error(
            `A fax is success, processing, transmitting or failed - not ${String(one)}.`
          );
        }

        return match;
      });

      setValueRef.current("faxStatus", statuses);
      applied.push(statuses.join(" and "));
    }

    return applied.length === 0
      ? "Re-applied the fax filters"
      : `Showing faxes: ${applied.join(", ")}`;
  });
}
